import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Cancel,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ConfirmDialog,
  EmptyState,
  FormDialog,
  KpiCard,
  KpiGrid,
  PageContainer,
  PageHeader,
  StatusChip,
  TableSkeleton,
  statusLabel,
  statusTone,
} from '../components/ui';
import { JOB_TYPE_LABELS } from '../components/dataIngestion/runFacts';
import { jobTriggerLabel } from '../utils/displayLabels';
import { parseFirestoreTimestamp } from '../utils/firestoreDate';
import {
  IngestionJob,
  IngestionJobLog,
  IngestionJobStatus,
  IngestionJobType,
  integrationApiService,
} from '../services/integrationApi';

const PAGE_SIZE = 25;
const FETCH_LIMIT = 200;

const JOB_TYPES: IngestionJobType[] = [
  'price_refresh',
  'incremental_scrape',
  'full_scrape',
  'data_fill',
  'api_ingestion',
  'stock_refresh',
  'rescrape_missing',
  'market_pricing_refresh',
];

function jobTypeLabel(type: string): string {
  return JOB_TYPE_LABELS[type as IngestionJobType] || type.replace(/_/g, ' ');
}

function parseWhen(value: unknown): Date | null {
  return parseFirestoreTimestamp(value);
}

function formatWhen(value: unknown): string {
  const date = parseWhen(value);
  if (!date) return '—';
  return format(date, 'MMM d, yyyy h:mm a');
}

function durationMs(job: IngestionJob): number | null {
  const start = parseWhen(job.startedAt || job.createdAt);
  const end = parseWhen(job.completedAt) || (job.status === 'running' ? new Date() : null);
  if (!start || !end) return null;
  const ms = end.getTime() - start.getTime();
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  return `${(ms / 3_600_000).toFixed(1)}h`;
}

function triggerLabel(job: IngestionJob): string {
  const raw = String(job.triggeredBy || '');
  if (raw.includes('scheduled_price_refresh')) return 'Daily price refresh';
  if (raw.includes('price_refresh_queue')) return 'Stale-SKU drain';
  if (raw.includes('scheduled_product_discovery')) return 'Discovery';
  if (raw.includes('stale_job_reaper')) return 'Reaper retry';
  if (raw.includes('announcement')) return 'Announcement';
  if (raw === 'schedule' || raw === 'scheduled') return 'Schedule';
  return jobTriggerLabel(raw);
}

function jobTitle(job: IngestionJob): string {
  if (job.type === 'data_fill') return job.brandName || '3P fill (B&H)';
  return job.brandName || job.brandId || 'Catalog job';
}

function processed(job: IngestionJob): string {
  const current = job.progress?.current ?? job.result?.productsProcessed ?? 0;
  const total = job.progress?.total;
  const updated = job.result?.productsUpdated;
  if (typeof total === 'number' && total > 0) return `${current} / ${total}`;
  if (job.status === 'completed' && updated != null) {
    return `${current} processed · ${updated} updated`;
  }
  return current ? `${current} processed` : '—';
}

export const AdminJobsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const jobIdParam = (searchParams.get('jobId') || '').trim();
  const [status, setStatus] = useState<string>(searchParams.get('status') || '');
  const [type, setType] = useState<string>(searchParams.get('type') || '');
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [page, setPage] = useState(0);
  const [selectedJob, setSelectedJob] = useState<IngestionJob | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);

  const { data: jobs = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin-ingestion-jobs'],
    queryFn: () => integrationApiService.getIngestionJobs(undefined, FETCH_LIMIT),
    refetchInterval: 15_000,
  });

  const { data: deepLinkedJob } = useQuery({
    queryKey: ['admin-ingestion-job', jobIdParam],
    queryFn: () => integrationApiService.getIngestionJob(jobIdParam),
    enabled: Boolean(jobIdParam) && !jobs.some((job) => job.id === jobIdParam),
    retry: false,
  });

  const { data: jobLogs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['admin-ingestion-job-logs', selectedJob?.id],
    queryFn: () => integrationApiService.getIngestionJobLogs(selectedJob!.id),
    enabled: Boolean(selectedJob) && logsOpen,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => integrationApiService.cancelIngestionJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ingestion-jobs'] });
    },
  });

  const mergedJobs = useMemo(() => {
    if (deepLinkedJob && !jobs.some((job) => job.id === deepLinkedJob.id)) {
      return [deepLinkedJob, ...jobs];
    }
    return jobs;
  }, [jobs, deepLinkedJob]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return mergedJobs.filter((job) => {
      if (status && job.status !== status) return false;
      if (type && job.type !== type) return false;
      if (!q) return true;
      return (
        job.brandName?.toLowerCase().includes(q) ||
        job.brandId?.toLowerCase().includes(q) ||
        job.id.toLowerCase().includes(q) ||
        jobTypeLabel(job.type).toLowerCase().includes(q) ||
        triggerLabel(job).toLowerCase().includes(q)
      );
    });
  }, [mergedJobs, status, type, search]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const completed = filtered.filter((job) => job.status === 'completed').length;
    const failed = filtered.filter((job) => job.status === 'failed').length;
    const running = filtered.filter((job) => job.status === 'running' || job.status === 'pending').length;
    return { total, completed, failed, running };
  }, [filtered]);

  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [status, type, search]);

  useEffect(() => {
    if (!jobIdParam) return;
    const hit = mergedJobs.find((job) => job.id === jobIdParam);
    if (!hit) return;
    setSelectedJob(hit);
    setLogsOpen(true);
  }, [jobIdParam, mergedJobs]);

  const openLogs = (job: IngestionJob) => {
    setSelectedJob(job);
    setLogsOpen(true);
    const next = new URLSearchParams(searchParams);
    next.set('jobId', job.id);
    setSearchParams(next, { replace: true });
  };

  const closeLogs = () => {
    setLogsOpen(false);
    const next = new URLSearchParams(searchParams);
    next.delete('jobId');
    setSearchParams(next, { replace: true });
  };

  return (
    <PageContainer>
      <PageHeader
        title="Jobs"
        description="Catalog scrapes, daily price refresh, discovery, and 3P fills."
        actions={
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
        }
      />

      <KpiGrid>
        <KpiCard label="Shown" value={stats.total} note="matching filters" />
        <KpiCard label="In flight" value={stats.running} tone={stats.running ? 'info' : 'none'} />
        <KpiCard label="Completed" value={stats.completed} tone="positive" />
        <KpiCard
          label="Failed"
          value={stats.failed}
          tone={stats.failed ? 'problem' : 'none'}
        />
      </KpiGrid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
          <TextField
            size="small"
            label="Search brand, type, or job id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All statuses</MenuItem>
              {(['pending', 'running', 'completed', 'failed', 'cancelled'] as IngestionJobStatus[]).map(
                (value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
              <MenuItem value="">All types</MenuItem>
              {JOB_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {jobTypeLabel(value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {isLoading ? (
        <TableSkeleton rows={8} />
      ) : pageRows.length === 0 ? (
        <EmptyState
          title="No catalog jobs match"
          description="Try clearing filters. 3P fills show as B&H Fill; price refresh and discovery are listed by brand."
        />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((job) => {
                const highlighted = job.id === jobIdParam;
                const pct =
                  job.progress?.total && job.progress.total > 0
                    ? Math.min(100, Math.round((job.progress.current / job.progress.total) * 100))
                    : job.status === 'completed'
                      ? 100
                      : 0;
                return (
                  <TableRow key={job.id} hover selected={highlighted}>
                    <TableCell>
                      <Typography variant="subtitle2">{jobTitle(job)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{jobTypeLabel(job.type)}</TableCell>
                    <TableCell>
                      <StatusChip label={statusLabel(job.status)} tone={statusTone(job.status)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      {(job.status === 'running' || job.status === 'pending') && (
                        <LinearProgress variant="determinate" value={pct} sx={{ mb: 0.5 }} />
                      )}
                      <Typography variant="body2">{processed(job)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={triggerLabel(job)} />
                    </TableCell>
                    <TableCell>{formatDuration(durationMs(job))}</TableCell>
                    <TableCell>{formatWhen(job.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View logs">
                          <IconButton size="small" onClick={() => openLogs(job)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(job.status === 'running' || job.status === 'pending') && (
                          <Tooltip title="Cancel">
                            <IconButton size="small" color="error" onClick={() => setCancelId(job.id)}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_event, next) => setPage(next)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </TableContainer>
      )}

      <FormDialog
        open={logsOpen}
        onClose={closeLogs}
        onSubmit={closeLogs}
        title={selectedJob ? `${jobTitle(selectedJob)} · ${jobTypeLabel(selectedJob.type)}` : 'Job'}
        submitLabel="Close"
        hideCancel
        maxWidth="md"
      >
          {selectedJob && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <StatusChip label={statusLabel(selectedJob.status)} tone={statusTone(selectedJob.status)} />
                <Chip size="small" label={triggerLabel(selectedJob)} />
                <Chip size="small" variant="outlined" label={selectedJob.id} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {processed(selectedJob)} · {formatDuration(durationMs(selectedJob))} · created{' '}
                {formatWhen(selectedJob.createdAt)}
              </Typography>
              {selectedJob.brandId && selectedJob.brandId !== 'bhphoto' && (
                <Link
                  component={RouterLink}
                  to={`/app/admin/data-ingestion?brand=${encodeURIComponent(selectedJob.brandId)}`}
                >
                  Open {selectedJob.brandName || selectedJob.brandId} in Data Ingestion
                </Link>
              )}
              {selectedJob.result?.errors?.length ? (
                <Alert severity="error">{selectedJob.result.errors[0]}</Alert>
              ) : null}
              <Box>
                <Typography variant="overline">Logs</Typography>
                {logsLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading logs…
                  </Typography>
                ) : jobLogs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No execution logs on this job.
                  </Typography>
                ) : (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {jobLogs.map((log: IngestionJobLog, index: number) => (
                      <Alert
                        key={`${log.timestamp}-${index}`}
                        severity={log.level === 'error' ? 'error' : log.level === 'warn' ? 'warning' : 'info'}
                      >
                        <Typography variant="body2">
                          <strong>{formatWhen(log.timestamp)}</strong> {log.message}
                        </Typography>
                      </Alert>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
      </FormDialog>

      <ConfirmDialog
        open={cancelId !== null}
        title="Cancel job"
        message="Cancel this catalog job? A pending Cloud Run claim will stop; a run that already started may still finish its current SKU."
        confirmLabel="Cancel job"
        cancelLabel="Keep running"
        confirmColor="error"
        loading={cancelMutation.isPending}
        onConfirm={() => {
          if (cancelId) {
            cancelMutation.mutate(cancelId, { onSettled: () => setCancelId(null) });
          }
        }}
        onClose={() => setCancelId(null)}
      />
    </PageContainer>
  );
};

export default AdminJobsPage;
